import React from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { InterviewPreview } from '../types/interviews';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { budgetCountries } from '../data/budget-countries';
import { InterviewCard } from '../components/interviews/InterviewCard';
import { useAuth } from '../lib/auth-context';
import { fetchInterviews } from '../lib/api';
import { Users, ArrowUpDown, ChevronDown, Globe2 } from 'lucide-react';
import * as amplitude from '@amplitude/analytics-browser';

const ITEMS_PER_PAGE = 9;

const getCountryFlagUrl = (countryCode: string): string => {
  const countryCodeMap: { [key: string]: string } = {
    'aus': 'au', 'nz': 'nz', 'ca': 'ca', 'jpn': 'jp', 'kor': 'kr',
    'twn': 'tw', 'arg': 'ar', 'chl': 'cl', 'col': 'co', 'mex': 'mx',
    'per': 'pe', 'uru': 'uy', 'bra': 'br', 'hkg': 'hk', 'fra': 'fr', 'ecu': 'ec'
  };
  const isoCode = countryCodeMap[countryCode.toLowerCase()] || countryCode.toLowerCase();
  return `https://flagcdn.com/w20/${isoCode}.png`;
};

const apiCountryCodes: { [key: string]: string } = {
  'aus': 'australie', 'nz': 'nouvelle-zelande', 'ca': 'canada', 'jpn': 'japon',
  'kor': 'coree-du-sud', 'twn': 'taiwan', 'arg': 'argentine', 'chl': 'chilie',
  'col': 'colombie', 'mex': 'mexique', 'per': 'perou', 'uru': 'uruguay',
  'bra': 'bresil', 'hkg': 'hong-kong', 'fra': 'france', 'ecu': 'equateur'
};

const apiToInternalCode: { [key: string]: string } = {
  'australie': 'aus', 'nouvelle-zelande': 'nz', 'canada': 'ca', 'japon': 'jpn',
  'coree-du-sud': 'kor', 'taiwan': 'twn', 'argentine': 'arg', 'chilie': 'chl',
  'colombie': 'col', 'mexique': 'mex', 'perou': 'per', 'uruguay': 'uru',
  'bresil': 'bra', 'hong-kong': 'hkg', 'france': 'fra', 'equateur': 'ecu'
};

export function InterviewsPage() {
  const { user, userInfo } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [interviews, setInterviews] = React.useState<InterviewPreview[]>([]);
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [currentPage, setCurrentPage] = React.useState(1);
  const [hasMore, setHasMore] = React.useState(true);
  const [sortBy, setSortBy] = React.useState<'date' | 'popularity'>('date');
  const [showSortMenu, setShowSortMenu] = React.useState(false);
  const [showCountryDropdown, setShowCountryDropdown] = React.useState(false);
  const observerTarget = React.useRef<HTMLDivElement>(null);
  const countryDropdownRef = React.useRef<HTMLDivElement>(null);
  const sortMenuRef = React.useRef<HTMLDivElement>(null);

  const selectedCountry = searchParams.get('country');

  const selectedCountryData = React.useMemo(() => {
    if (!selectedCountry) return null;
    const internalCode = apiToInternalCode[selectedCountry] || selectedCountry;
    return budgetCountries.find(c => c.code.toLowerCase() === internalCode.toLowerCase());
  }, [selectedCountry]);

  React.useEffect(() => {
    amplitude.track("Stories Displayed");
  }, []);

  React.useEffect(() => {
    if (!user) {
      navigate('/', { replace: true });
    }
  }, [user, navigate]);

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (countryDropdownRef.current && !countryDropdownRef.current.contains(event.target as Node)) {
        setShowCountryDropdown(false);
      }
      if (sortMenuRef.current && !sortMenuRef.current.contains(event.target as Node)) {
        setShowSortMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchInterviewsData = React.useCallback(async (page: number) => {
    if (!userInfo) return;

    try {
      const data = await fetchInterviews(page, ITEMS_PER_PAGE, selectedCountry, sortBy, userInfo);
      const newInterviews = Array.isArray(data) ? data : data.error ? [] : [data];

      if (newInterviews.length < ITEMS_PER_PAGE) {
        setHasMore(false);
      }

      setInterviews(prev => page === 1 ? newInterviews : [...prev, ...newInterviews]);
    } catch (error) {
      console.error('Error fetching interviews:', error);
      setError('Impossible de charger les interviews pour le moment');
    } finally {
      setIsLoading(false);
    }
  }, [selectedCountry, sortBy, userInfo]);

  React.useEffect(() => {
    if (user && userInfo) {
      setIsLoading(true);
      setCurrentPage(1);
      setHasMore(true);
      fetchInterviewsData(1);
    }
  }, [fetchInterviewsData, user, userInfo]);

  React.useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting && hasMore && !isLoading) {
          setCurrentPage(prev => prev + 1);
        }
      },
      { threshold: 0.1 }
    );

    if (observerTarget.current) {
      observer.observe(observerTarget.current);
    }

    return () => observer.disconnect();
  }, [hasMore, isLoading]);

  React.useEffect(() => {
    if (currentPage > 1) {
      fetchInterviewsData(currentPage);
    }
  }, [currentPage, fetchInterviewsData]);

  const handleCountrySelect = (countryCode: string | null) => {
    if (countryCode) {
      const apiCode = apiCountryCodes[countryCode.toLowerCase()] || countryCode.toLowerCase();
      setSearchParams({ country: apiCode });
    } else {
      setSearchParams({});
    }
    setCurrentPage(1);
    setInterviews([]);
    setHasMore(true);
    setShowCountryDropdown(false);
  };

  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col pb-mobile overflow-x-hidden">
      <Navbar
        onLogin={() => {}}
        onAdminClick={() => {}}
        onHomeClick={() => {}}
        isAdminMode={false}
      />

      <div className="relative min-h-[280px] sm:min-h-[340px] flex items-end">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1517457373958-b7bdd4587205?q=80&w=2669&auto=format&fit=crop"
            alt=""
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-gray-900/70 via-gray-900/60 to-gray-900/90" />
        </div>

        <div className="relative w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8 sm:pb-10 pt-24 sm:pt-28">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 mb-4 sm:mb-5">
              <Users className="w-4 h-4 text-teal-400" />
              <span className="text-xs sm:text-sm font-medium text-white">Recits de voyageurs PVT</span>
            </div>

            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-3 sm:mb-4">
              Ils racontent leur experience PVT
            </h1>

            <p className="text-sm sm:text-base text-white/85 leading-relaxed max-w-2xl">
              Decouvrez les temoignages de pvtistes qui partagent leur experience, leurs conseils et leur parcours a l'etranger.
            </p>
          </div>
        </div>
      </div>

      <main className="flex-1">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-200/50 dark:border-gray-700/50 -mt-6 relative z-10 p-4 sm:p-6">
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
              <div className="relative" ref={countryDropdownRef}>
                <button
                  onClick={() => setShowCountryDropdown(!showCountryDropdown)}
                  className="flex items-center gap-2 px-4 py-2 rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors text-sm"
                >
                  {selectedCountryData ? (
                    <>
                      <img
                        src={getCountryFlagUrl(selectedCountryData.code)}
                        alt={selectedCountryData.name}
                        className="w-4 h-auto rounded-sm"
                      />
                      <span className="font-medium text-gray-700 dark:text-gray-300">{selectedCountryData.name}</span>
                    </>
                  ) : (
                    <>
                      <Globe2 className="w-4 h-4 text-gray-400" />
                      <span className="font-medium text-gray-700 dark:text-gray-300">Tous les pays</span>
                    </>
                  )}
                  <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${showCountryDropdown ? 'rotate-180' : ''}`} />
                </button>

                {showCountryDropdown && (
                  <div className="absolute z-50 w-64 mt-2 bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
                    <div className="max-h-80 overflow-y-auto">
                      <button
                        onClick={() => handleCountrySelect(null)}
                        className={`w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors ${
                          !selectedCountry ? 'bg-blue-50 dark:bg-blue-900/20' : ''
                        }`}
                      >
                        <Globe2 className={`w-4 h-4 ${!selectedCountry ? 'text-blue-500' : 'text-gray-400'}`} />
                        <span className={`text-sm font-medium ${!selectedCountry ? 'text-blue-700 dark:text-blue-300' : 'text-gray-700 dark:text-gray-300'}`}>
                          Tous les pays
                        </span>
                      </button>
                      <div className="h-px bg-gray-100 dark:bg-gray-700 mx-3" />
                      {budgetCountries.map((country) => (
                        <button
                          key={country.code}
                          onClick={() => handleCountrySelect(country.code)}
                          className={`w-full flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors ${
                            selectedCountryData?.code === country.code ? 'bg-blue-50 dark:bg-blue-900/20' : ''
                          }`}
                        >
                          <img
                            src={getCountryFlagUrl(country.code)}
                            alt={country.name}
                            className="w-4 h-auto rounded-sm"
                            loading="lazy"
                          />
                          <span className={`text-sm font-medium ${
                            selectedCountryData?.code === country.code
                              ? 'text-blue-700 dark:text-blue-300'
                              : 'text-gray-700 dark:text-gray-300'
                          }`}>
                            {country.name}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="flex-1" />

              <div className="relative" ref={sortMenuRef}>
                <button
                  onClick={() => setShowSortMenu(!showSortMenu)}
                  className="flex items-center gap-2 px-4 py-2.5 bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors text-sm"
                >
                  <ArrowUpDown className="w-4 h-4 text-gray-500" />
                  <span className="font-medium text-gray-700 dark:text-gray-300">
                    {sortBy === 'date' ? 'Plus recents' : 'Populaires'}
                  </span>
                  <ChevronDown className="w-4 h-4 text-gray-400" />
                </button>

                {showSortMenu && (
                  <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 py-1 z-10 overflow-hidden">
                    <button
                      onClick={() => {
                        setSortBy('date');
                        setShowSortMenu(false);
                        setCurrentPage(1);
                        setInterviews([]);
                        setHasMore(true);
                      }}
                      className={`w-full px-4 py-2.5 text-left text-sm font-medium transition-colors ${
                        sortBy === 'date'
                          ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                          : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                      }`}
                    >
                      Plus recents
                    </button>
                    <button
                      onClick={() => {
                        setSortBy('popularity');
                        setShowSortMenu(false);
                        setCurrentPage(1);
                        setInterviews([]);
                        setHasMore(true);
                      }}
                      className={`w-full px-4 py-2.5 text-left text-sm font-medium transition-colors ${
                        sortBy === 'popularity'
                          ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                          : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                      }`}
                    >
                      Populaires
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="py-8">
            {isLoading && interviews.length === 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="animate-pulse">
                    <div className="bg-white dark:bg-gray-800 rounded-2xl overflow-hidden border border-gray-200/50 dark:border-gray-700/50 shadow-sm">
                      <div className="aspect-[16/9] bg-gray-200 dark:bg-gray-700 relative overflow-hidden">
                        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent animate-pulse" />
                      </div>
                      <div className="p-5 space-y-3">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-10 h-10 bg-gray-200 dark:bg-gray-700 rounded-full" />
                          <div className="flex-1 space-y-2">
                            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded-lg w-3/4" />
                            <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded-lg w-1/2" />
                          </div>
                        </div>
                        <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded-lg w-full" />
                        <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded-lg w-2/3" />
                        <div className="flex items-center gap-2 pt-3 border-t border-gray-100 dark:border-gray-700/50">
                          <div className="w-6 h-6 bg-gray-200 dark:bg-gray-700 rounded-full" />
                          <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded-lg w-20" />
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : error ? (
              <div className="bg-white dark:bg-gray-800 rounded-2xl border border-red-200 dark:border-red-800/50 p-8 text-center">
                <div className="w-12 h-12 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center mx-auto mb-4">
                  <Users className="w-6 h-6 text-red-500" />
                </div>
                <p className="text-red-600 dark:text-red-400 font-medium">{error}</p>
              </div>
            ) : interviews.length === 0 ? (
              <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-12 text-center">
                <div className="w-16 h-16 rounded-2xl bg-gray-100 dark:bg-gray-700 flex items-center justify-center mx-auto mb-5">
                  <Users className="w-8 h-8 text-gray-400" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                  {selectedCountry
                    ? 'Aucun recit pour ce pays'
                    : 'Aucun recit disponible'}
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 max-w-md mx-auto">
                  {selectedCountry
                    ? 'Essayez un autre pays ou revenez plus tard'
                    : 'De nouveaux recits seront bientot disponibles'}
                </p>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {interviews.map((interview) => (
                    <InterviewCard key={interview.id} interview={interview} />
                  ))}
                </div>

                {hasMore && (
                  <div
                    ref={observerTarget}
                    className="flex justify-center py-10"
                  >
                    {isLoading && (
                      <div className="animate-spin rounded-full h-8 w-8 border-2 border-teal-500 border-t-transparent" />
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
